import { Cartesian3 } from "cesium";
import { EarthBrainViewerAPIs } from "./EarthBrainViewerAPIs";
export class RoadConstructionAPIs extends EarthBrainViewerAPIs {
    /*
     * Returns length of the road
     * @param roadID
     */
    getLengthOfRoad(roadID) {
        return this._scDesign.getLengthOfRoad(roadID);
    }
    getControlPointIndexById(roadId, pointId) {
        return this._scDesign.getControlPointIndexById(roadId, pointId);
    }
    isRoadLoaded(roadId) {
        return this._scDesign.isRoadLoaded(roadId);
    }
    getRoadPointLength(roadID) {
        return this._scDesign.getRoadPointLength(roadID);
    }
    /**
     * Returns the object containing minimum slope value and maximum slope value of the road
     * @param roadID
     * @returns
     */
    getRoadSlopeInfo(roadID) {
        return this._scDesign.getRoadSlopeInfo(roadID);
    }
    /**
     * Returns the object containing minimum elevation and maximum elevation of the road
     * @param roadID
     * @returns
     */
    getRoadHeightInfo(roadID) {
        return this._scDesign.getRoadHeightInfo(roadID);
    }
    getRoadMeshGeometry(roadId) {
        return this._scDesign.getRoadMeshGeometry(roadId);
    }
    getPolylineVolumeGeometry(roadId) {
        return this._scDesign.getPolylineVolumeGeometry(roadId);
    }
    /**
     * Function returns Control point coordinates
     * @param roadID Flat ground ID
     * @param controlPointId Flat ground Control point ID whose coordinates we require
     * @returns Cesium.Cartesian3 values of control point
     */
    getCtrlPointCordiOnRoad(roadID, controlPointId) {
        return this._scDesign.getCtrlPointCordiOnRoad(roadID, controlPointId);
    }
    /**
     * This function will calculate volume of Road
     * @param roadID ID of Road whose volume to calculate
     * @param cellX Optional as considered unit grid size 0.5 as required unit area for grid is 0.25 sq.m
     * @param cellY Optional as considered unit grid size 0.5 as required unit area for grid is 0.25 sq.m
     * @param cellZ Optional as considered unit grid size 0.5 as required unit area for grid is 0.25 sq.m
     * @returns Fill and Cut Volume in cu. meter.
     */
    async volumeCalculationForRoad(roadID) {
        return await this._scDesign.volumeCalculationForRoad(roadID);
    }
    /**
     * Saves Road object as JSON string and return that string
     * @param roadID
     * @returns
     */
    getRoadJSON(roadID) {
        return this._scDesign.getRoadJSON(roadID);
    }
    /**
     * Returns Road linear data as JSON string and return that string
     * @param roadInfoObject contains all params necessary for generating Linear Data json.
     * @returns string
     */
    getRoadLinearData(roadInfoObject) {
        return this._scDesign.getRoadLinearData(roadInfoObject);
    }
    //Creation
    /**
     * Creates Road using json string and return road id
     * @param jsonString
     * @returns
     */
    async loadRoadJSON(roadData, stationInterval, updateInListCallback) {
        return await this._scDesign.loadRoadInfo(roadData, stationInterval, updateInListCallback);
    }
    /**
     * This function will create a road with provided array of control points of road
     * @param controlPointArray Array of control points
     */
    createRoadFromPoints(controlPointArray, callback, associatePointCloudId) {
        this._scDesign.createRoadFromPoints(controlPointArray, callback, associatePointCloudId);
    }
    /**
     * AI が生成した経路から仮設道路を作成し、作成後に callback へ道路 ID を渡す。
     * 失敗（点群が見つからない場合を含む）は返り値の Promise の reject で通知するため、呼び出し側は await する。
     */
    createRoadFromPointsWithAI(roadPoints, controlPointArray, callback, associatePointCloudId) {
        return this._scDesign.createRoadFromPointsWithAI(roadPoints, controlPointArray, callback, associatePointCloudId);
    }
    enablePathCreation(callback, associatedTerrain, associateRubbingDestination) {
        this._scDesign.enablePathCreation(callback, associatedTerrain, associateRubbingDestination);
    }
    enablePathCreationWithClickCount(callback, associatedTerrain, associateRubbingDestination, clickCount, associatePointCloudId, associateDrawing2dImageId) {
        if (associatePointCloudId !== undefined ||
            associateDrawing2dImageId !== undefined) {
            this._scDesign.enablePathCreationWithClickCount(callback, associatedTerrain, associateRubbingDestination, clickCount, associatePointCloudId, associateDrawing2dImageId);
        }
        else {
            throw {
                errorId: "params-error",
                message: `AssociatePointCloud is ${associatePointCloudId}. Please provide the correct id (AI)`,
            };
        }
    }
    /**
     * Add Control Point on Road before the control point on which this function is called
     * @param roadID ID Of the road to update the settings
     *
     */
    addControlPointOnRoad(roadID, controlPointId) {
        this._scDesign.addControlPointOnRoad(roadID, controlPointId);
    }
    /**
     * This Function will let you add a new control point on the given point on the center line
     * @param roadID unique ID of the road
     * @param localPoint point on center line of the road in local space
     */
    addControlPointOnCenterLineOfRoad(roadId, localPoint) {
        this._scDesign.addControlPointOnCenterLineOfRoad(roadId, Cartesian3.unpack(localPoint));
    }
    /**
     * This Function will check whether there is any road under creation.
     * @returns True or False for road under creation or not.
     */
    checkRoadUnderCreation() {
        return this._scDesign.checkRoadUnderCreation();
    }
    //Edit
    enableRoadEdit(status, roadID, updateCallback, onDoubleClickOnSlopeTextCallback) {
        this._scDesign.enableRoadEdit(status, roadID, updateCallback, onDoubleClickOnSlopeTextCallback);
    }
    updateRoadHorizontalAlignmentType(roadId, horizontalAlignmentType) {
        this._scDesign.updateRoadHorizontalAlignmentType(roadId, horizontalAlignmentType);
    }
    /**
     * Function to updates coordinates values of control points
     * @param roadID Road ID
     * @param controlPointID Road Control point ID whose coordinates we will update
     * @param x by this x value we need to update control point value
     * @param y by this y value we need to update control point value
     * @param z by this z value we need to update control point value
     */
    updateCtrlPointCordiOnRoad(roadID, controlPointID, x, y, z) {
        this._scDesign.updateCtrlPointCordiOnRoad(roadID, controlPointID, new Cartesian3(x, y, z));
    }
    /**
     * Update the settings for existing road
     * @param roadID ID Of the road to update the settings
     * @param settings setting's object
     */
    async updateRoadSettings(roadID, settings) {
        await this._scDesign.updateRoadSettings(roadID, settings);
    }
    // public async restoreRoadAfterHiding(roadID: string): Promise<void> {
    // 	await this._scDesign.restoreRoadAfterHiding(roadID);
    // }
    updateRoadMaterialSettings(roadID, commonMaterial, embarkmentMaterial, cutMaterial) {
        this._scDesign.updateRoadMaterialSettings(roadID, commonMaterial, embarkmentMaterial, cutMaterial);
    }
    /**
     * This Function will Disable Road Creation.
     * @param roadUnderConstruction Pass a parameter of type 'Road' to this function to disable road creation.
     */
    finishRoadConstruction() {
        this._scDesign.finishRoadConstruction();
    }
    cancelRoadCreation() {
        this._scDesign.cancelRoadCreation();
    }
    /**
     * Deletes the road mesh, control points, control lines and polyline attached with road
     * @param roadID unique ID of the road
     */
    deleteRoad(roadID) {
        this._scDesign.deleteRoad(roadID);
    }
    //road edit
    removeControlPoint(roadID, controlPointId) {
        this._scDesign.removeControlPoint(roadID, controlPointId);
    }
    async removeSelectedSlopeBreak(roadID) {
        await this._scDesign.removeSelectedSlopeBreak(roadID);
    }
    /**
     * Force-recalculate road geom
     * @param roadId
     */
    recalculateRoadGeometry(roadId) {
        this._scDesign.recalculateRoadGeometry(roadId);
    }
    /**
     * Is Road Control Point Removable
     * @param roadId Id of road
     * @param controlPointId Id of control Point
     * @returns Returns true if road control point is removable
     */
    isRoadControlPointRemovable(roadId, controlPointId) {
        return this._scDesign.isRoadControlPointRemovable(roadId, controlPointId);
    }
    async updateTerrainAssociatedWithRoad(roadId, terrainId, terrainType) {
        await this._scDesign.updateTerrainAssociatedWithObject(roadId, "road", terrainId, terrainType);
    }
    async updateRubbingDestinationAssociatedWithRoad(roadId, rubbingDestination) {
        await this._scDesign.updateRubbingDestinationAssociatedWithObject(roadId, "road", rubbingDestination);
    }
    getCorrespondingProjectedDistFromStart(roadId, distanceFromStart) {
        return this._scDesign.getCorrespondingProjectedDistFromStart(roadId, distanceFromStart);
    }
    setRoadAlignmentVisibility(roadID, visible) {
        this._scDesign.setRoadAlignmentVisibility(roadID, visible);
    }
    setRoadIpLineVisibility(roadID, visible) {
        this._scDesign.setRoadIpLineVisibility(roadID, visible);
    }
    setRoadCenterLineVisibility(roadID, visible) {
        this._scDesign.setRoadCenterLineVisibility(roadID, visible);
    }
    setRoadSurfaceVisibility(roadID, visible) {
        this._scDesign.setRoadSurfaceVisibility(roadID, visible);
    }
    setRoadEmbankmentVisibility(roadID, visible) {
        this._scDesign.setRoadEmbankmentVisibility(roadID, visible);
    }
    setRoadFillEmbankmentVisibility(roadID, visible) {
        this._scDesign.setRoadFillEmbankmentVisibility(roadID, visible);
    }
    setRoadCutEmbankmentVisibility(roadID, visible) {
        this._scDesign.setRoadCutEmbankmentVisibility(roadID, visible);
    }
}
//# sourceMappingURL=road-construction.js.map